import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { FiPlus, FiCreditCard, FiEdit2 } from 'react-icons/fi';
import PageHeader from '../../components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Select } from '../../components/ui/Input';
import FormField from '../../components/ui/FormField';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import ErrorBanner from '../../components/ui/ErrorBanner';
import StatusPill from '../../components/ui/StatusPill';
import AppLayout from '../../components/layout/AppLayout';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { humanizeEnum } from '../../utils/format';
import { listInvoices, getInvoice } from '../../api/invoices';
import { listPaymentsForInvoice } from '../../api/payments';
import InvoiceSummaryCard from './InvoiceSummaryCard';
import RecordPaymentForm from './RecordPaymentForm';

function remainingOf(inv) {
  if (!inv) return 0;
  const total = Number(inv.totalAmount ?? inv.amount ?? 0);
  const paid = Number(inv.paidAmount ?? 0);
  return Math.max(total - paid, 0);
}

export default function PaymentsListPage() {
  const navigate = useNavigate();
  const { invoiceId: routeInvoiceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryInvoiceId = searchParams.get('invoiceId');
  const initialId = routeInvoiceId || queryInvoiceId || '';

  const [selectedId, setSelectedId] = useState(initialId);
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);

  const handleEditPayment = (payment) => {
    setPaymentToEdit(payment);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setPaymentToEdit(null);
  };

  const idNum = selectedId ? Number(selectedId) : null;

  const invoicesQuery = useQuery({
    queryKey: ['invoices'],
    queryFn: () => listInvoices(),
    staleTime: 30_000,
  });

  const invoiceQuery = useQuery({
    queryKey: ['invoices', idNum],
    queryFn: () => getInvoice(idNum),
    enabled: !!idNum,
  });

  const paymentsQuery = useQuery({
    queryKey: ['payments', idNum],
    queryFn: () => listPaymentsForInvoice(idNum),
  });

  const invoiceOptions = useMemo(() => {
    const list = invoicesQuery.data || [];
    return list.map((inv) => {
      const code = inv.invoiceNumber || `INV-${inv.id}`;
      return {
        value: inv.id,
        label: `${code} · Cust ${inv.customerId ?? '—'} · Balance ${
          // Inline currency for option label (option text can't host React)
          remainingOf(inv).toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2,
          })
        }`,
      };
    });
  }, [invoicesQuery.data]);

  const handlePick = (value) => {
    setSelectedId(value);
    if (!value) {
      setSearchParams({});
      if (routeInvoiceId) {
        navigate('/finance/payments');
      }
      return;
    }
    if (routeInvoiceId) {
      navigate(`/finance/payments/invoice/${value}`);
    } else {
      setSearchParams({ invoiceId: String(value) });
    }
  };

  const columns = [
    {
      key: 'paidAt',
      header: 'Date',
      render: (row) => (
        <DateDisplay value={row.paidAt || row.createdAt} withTime />
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => (
        <CurrencyDisplay value={row.amount} className="font-medium" />
      ),
    },
    {
      key: 'paymentMethod',
      header: 'Method',
      render: (row) => (
        <span className="text-sm text-ink">
          {humanizeEnum(row.paymentMethod || row.paymentMode)}
        </span>
      ),
    },
    {
      key: 'referenceNo',
      header: 'Reference',
      render: (row) =>
        row.referenceNo ? (
          <span className="font-mono text-xs text-ink-muted">
            {row.referenceNo}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill value={row.status} />,
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (row) =>
        row.notes ? (
          <span className="line-clamp-2 max-w-xs text-xs text-ink-muted">
            {row.notes}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          icon={FiEdit2}
          onClick={() => handleEditPayment(row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const payments = paymentsQuery.data || [];
  const totalReceived = payments
    .filter((p) => p.status === 'SUCCESS' || p.status === 'PENDING')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  return (
    <AppLayout title="Payments" breadcrumb="Finance / Payments">
      <div className="space-y-6">
        <PageHeader
          title="Payments"
          description="Browse and record payments against customer invoices."
          actions={
            <Button
              variant="primary"
              icon={FiPlus}
              onClick={() => {
                setPaymentToEdit(null);
                setModalOpen(true);
              }}
            >
              Record Payment
            </Button>
          }
        />

        <Card>
          <CardHeader
            title="Select an invoice"
            subtitle="Choose an invoice to view its payment history."
          />
          <CardBody>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
              <FormField label="Invoice">
                <Select
                  value={selectedId}
                  onChange={(e) => handlePick(e.target.value)}
                  disabled={invoicesQuery.isLoading}
                >
                  <option value="">
                    {invoicesQuery.isLoading
                      ? 'Loading invoices…'
                      : 'All invoices'}
                  </option>
                  {invoiceOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormField>
              {selectedId && (
                <Button
                  variant="outline"
                  onClick={() => handlePick('')}
                  className="sm:mb-[1px]"
                >
                  Clear
                </Button>
              )}
            </div>
            {invoicesQuery.isError && (
              <div className="mt-3">
                <ErrorBanner
                  error={invoicesQuery.error}
                  title="Could not load invoices"
                  onRetry={() => invoicesQuery.refetch()}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {selectedId && (
          <>
            {invoiceQuery.isError ? (
              <ErrorBanner
                error={invoiceQuery.error}
                title="Could not load invoice"
                onRetry={() => invoiceQuery.refetch()}
              />
            ) : (
              <InvoiceSummaryCard
                invoice={invoiceQuery.data}
                loading={invoiceQuery.isLoading}
              />
            )}
          </>
        )}

        <Card>
          <CardHeader
            title={selectedId ? 'Payment History' : 'All Payments'}
            subtitle={
              payments.length > 0 ? (
                <span>
                  {payments.length} record{payments.length === 1 ? '' : 's'} ·
                  Received{' '}
                  <CurrencyDisplay
                    value={totalReceived}
                    className="font-semibold text-ink"
                  />
                </span>
              ) : selectedId ? (
                'No payments yet for this invoice.'
              ) : (
                'No payments recorded yet.'
              )
            }
            actions={
              <Button
                variant="primary"
                size="sm"
                icon={FiPlus}
                onClick={() => {
                  setPaymentToEdit(null);
                  setModalOpen(true);
                }}
              >
                Record Payment
              </Button>
            }
          />
          <CardBody className="p-0">
            {paymentsQuery.isError ? (
              <div className="p-5">
                <ErrorBanner
                  error={paymentsQuery.error}
                  title="Could not load payments"
                  onRetry={() => paymentsQuery.refetch()}
                />
              </div>
            ) : (
              <DataTable
                columns={columns}
                rows={payments}
                loading={paymentsQuery.isLoading}
                empty="No payments recorded yet"
              />
            )}
          </CardBody>
        </Card>

        <RecordPaymentForm
          open={modalOpen}
          onClose={handleCloseModal}
          defaultInvoiceId={selectedId ? Number(selectedId) : null}
          editPayment={paymentToEdit}
        />
      </div>
    </AppLayout>
  );
}
